import { ConfigProvider } from 'antd'
import enUS from 'antd/es/locale/en_US'
import { cloneDeep } from 'lodash'
import { Suspense, useEffect } from 'react'
import { IdleTimerProvider } from 'react-idle-timer'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, Outlet, Route, Routes, useNavigate, useSearchParams } from 'react-router-dom'
import {
  APP_CHANNEL,
  APP_CHANNEL_ACTIONS,
  APP_IDLE_TIMEOUT_SECONDS,
  defaultHomePage,
} from 'src/app/shared/constants/app.constants'
import { lcStorageUserRole } from './shared/constants/localStorage.constants'
import './App.scss'
import EmailRedirectGuard from './features/feature-authentication/email-redirect-guard'
import Login from './features/feature-authentication/login'
import OTPLogin from './features/feature-authentication/otp-login'
import ResetPassword from './features/feature-authentication/reset-password'
import BatchProcessing from './features/feature-business-admin-logs/batch-processing'
import SentEmailTextLogsSetup from './features/feature-business-admin-logs/sent-email-texts'
import SentEmailTextsResults from './features/feature-business-admin-logs/sent-email-texts/sent-email-texts-results'
import AuditTrailLog from './features/feature-business-admin-logs/audit-trail-log'
import BankSetupForm from './features/feature-business-administration/bank-user-setup/add-new-bank-user'
import BankSetupCopy from './features/feature-business-administration/bank-user-setup/copy-new-bank-user'
import BankSetupEdit from './features/feature-business-administration/bank-user-setup/edit-bank-user'
import LandingBankUserSetup from './features/feature-business-administration/bank-user-setup/landing-bank-user-setup'
import CheckVerification from './features/feature-business-administration/check-verification'
import PurgeTransactions from './features/feature-business-administration/purge-transactions'
import SystemWideRules from './features/feature-business-administration/system-wide-rules'
import UserRoles from './features/feature-business-administration/user-roles'
import AddUserRole from './features/feature-business-administration/user-roles/add-user-role'
import EditUserRole from './features/feature-business-administration/user-roles/edit-user-role'
import ViewUserRole from './features/feature-business-administration/user-roles/view-user-role'
import AccountSetup from './features/feature-client-setup/account-setup'
import EditAccountScreen from './features/feature-client-setup/account-setup/edit-account'
import NewAccountScreen from './features/feature-client-setup/account-setup/new-account'
import ViewAccountScreen from './features/feature-client-setup/account-setup/view-account'
import ViewUsersForAccount from './features/feature-client-setup/account-setup/view-users-for-account'
import ClientSetup from './features/feature-client-setup/client-setup'
import AddClientSetup from './features/feature-client-setup/client-setup/add-client-setup'
import EditClientSetup from './features/feature-client-setup/client-setup/edit-client-setup'
import ClientSetupViewRecord from './features/feature-client-setup/client-setup/view-record'
import AddClientUserSetup from './features/feature-client-setup/client-user-setup/add-client-user-setup'
import EditClientUserSetup from './features/feature-client-setup/client-user-setup/edit-client-user-setup'
import LandingClientUserSetup from './features/feature-client-setup/client-user-setup/landing-client-user-setup'
import FileMappingSetup from './features/feature-client-setup/file-mapping-setup'
import AddFileMapping from './features/feature-client-setup/file-mapping-setup/add-file-mapping'
import CopyFileMapping from './features/feature-client-setup/file-mapping-setup/copy-file-mapping'
import EditFileMapping from './features/feature-client-setup/file-mapping-setup/edit-file-mapping'
import ViewFileMapping from './features/feature-client-setup/file-mapping-setup/view-file-mapping'
import PayeeMatchParameters from './features/feature-client-setup/payee-match-parameters'
import AddNewPayeeMatchParameters from './features/feature-client-setup/payee-match-parameters/add-new-parameters'
import EditPayeeMatchParameters from './features/feature-client-setup/payee-match-parameters/edit-parameters'
import ViewPayeeMatchParameters from './features/feature-client-setup/payee-match-parameters/view-parameters'
import QuickExceptionSetup from './features/feature-exception-management/quick-exception-processing'
import AddHelpPage from './features/feature-help-page/add-help-page/AddHelpPage'
import EditHelpPage from './features/feature-help-page/edit-help-page/EditHelpPage'
import HelpPageManagement from './features/feature-help-page/help-page-management'
import PreviewHelpPage from './features/feature-help-page/preview-help-page/PreviewHelpPage'
import ViewVersionHistory from './features/feature-help-page/version-history/ViewVersionHistory'
import ViewHelpPage from './features/feature-help-page/view-help-page/ViewHelpPage'
import Home from './features/feature-mgmt'
import IssuedCheckLogResultScreen from './features/feature-transaction-reports/issued-check-log-results'
import IssuedCheckProcessingLogScreen from './features/feature-transaction-reports/issued-check-processing-log'
import ViewErrorScreen from './features/feature-transaction-reports/view-error'
import AddACHExceptionRule from './features/feature-transaction-rules/ach-exception-rules/add-exception-rule'
import EditACHExceptionRule from './features/feature-transaction-rules/ach-exception-rules/edit-exception-rule'
import LandingACHExceptionRules from './features/feature-transaction-rules/ach-exception-rules/landing-exception-rule/LandingACHExceptionRules'
import ViewACHExceptionRule from './features/feature-transaction-rules/ach-exception-rules/view-exception-rule'
import AddNewIssuedCheckScreen from './features/feature-transaction-rules/add-new-issued-check'
import AllTransactionScreen from './features/feature-transaction-rules/all-transactions'
import ViewAuditHistory from './features/feature-transaction-rules/all-transactions/view-audit-history'
import ViewTransactionContent from './features/feature-transaction-rules/all-transactions/view-record'
import SearchResultScreen from './features/feature-transaction-rules/search-result'
import SubmitIssuedCheckScreen from './features/feature-transaction-rules/submit-issued-check'
import VoidChecksScreen from './features/feature-transaction-rules/void-checks'
import { TransactionFiltersProvider } from './features/feature-transaction-rules/TransactionFiltersContext'
import { ClientSetupFiltersProvider } from './features/feature-client-setup/client-setup/ClientSetupFiltersContext'
import { AccountSetupFiltersProvider } from './features/feature-client-setup/account-setup/AccountSetupFiltersContext'
import { ClientUserSetupFiltersProvider } from './features/feature-client-setup/client-user-setup/ClientUserSetupFiltersContext'
import { FileMappingSetupFiltersProvider } from './features/feature-client-setup/file-mapping-setup/FileMappingSetupFiltersContext'
import { BankUserSetupFiltersProvider } from './features/feature-business-administration/bank-user-setup/BankUserSetupFiltersContext'
import AuthBaseLayout from './shared/components/layout/auth-base-layout/AuthBaseLayout'
import AppLayout from './shared/components/layout/menu-layout'
import AuthenRouterGuard from './shared/components/router-guard/AuthenRouterGuard'
import PermissionRouterGuard from './shared/components/router-guard/PermissionRouterGuard'
import { menuUrls } from './shared/constants/menu.constants'
import { useRolePermission } from './shared/hooks/useRolePermission'
import { ResetPasswordType } from './shared/models/common.model'
import { EPermissions, ESubModules } from './shared/models/roleAndPermission.model'
import AccessDenied from './shared/pages/access-denied-page/AccessDenied'
import InactiveUserPage from './shared/pages/access-denied-page/InactiveUserPage'
import LockUserPage from './shared/pages/access-denied-page/LockUserPage'
import LoggedOutBankAccountSuccess from './shared/pages/access-denied-page/LoggedOutBankAccountSuccess'
import LoggedOutForInactivity from './shared/pages/access-denied-page/LoggedOutForInactivity'
import PageNotFound from './shared/pages/access-denied-page/PageNotFound'
import SSOInactiveUser from './shared/pages/sso-landing-page/SSOInactiveUser'
import SSORedirecting from './shared/pages/sso-landing-page/SSORedirecting'
import SSOUnexpectedErrorOccured from './shared/pages/sso-landing-page/SSOUnexpectedErrorOccured'
import SSOUserLocked from './shared/pages/sso-landing-page/SSOUserLocked'
import SSOUserNotFound from './shared/pages/sso-landing-page/SSOUserNotFound'
import { getMe } from './shared/services/AuthenticationService'
import {
  fetchLookupValues,
  getCriticalSystemConfiguration,
  getDeferredConfiguration,
  getTimeZones,
} from './shared/services/MasterSetupService'
import { logOutApp } from './store'
import { getAccessToken, getUserProfile, setUserProfile } from './store/auth'
import { updateCriticalConfig, updateDeferredConfig, updateLookupValues, updateTimeZones } from './store/masterSetup'
import primaryThemeDefault from './themes/primaryThemeDefault'

export const appChannel = new BroadcastChannel(APP_CHANNEL)
function App() {
  const accessToken = useSelector(getAccessToken)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()
  const userProfile = useSelector(getUserProfile)
  const { userPermissions, role } = useRolePermission()

  useEffect(() => {
    const listener = (event: any) => {
      if (event?.data?.type === APP_CHANNEL_ACTIONS.LOGOUT) {
        sessionStorage.removeItem(lcStorageUserRole)
        dispatch(logOutApp())
        const cloneUserRole = cloneDeep(role ?? '')
        setTimeout(() => {
          navigate('/log-out', {
            state: {
              userRole: cloneUserRole,
            },
          })
        }, 100)
      }
    }

    appChannel.addEventListener('message', listener)

    return () => appChannel.removeEventListener('message', listener)
  }, [dispatch, navigate, role])

  // get master data
  useEffect(() => {
    if (accessToken) {
      fetchLookupValues().then((res: any) => {
        if (res?.data) {
          dispatch(updateLookupValues(res.data))
        }
      })

      getTimeZones().then((res: any) => {
        if (res?.data) {
          dispatch(
            updateTimeZones(res.data?.sort((a: any, b: any) => a?.applicationName.localeCompare(b?.applicationName))),
          )
        }
      })
    }
  }, [accessToken, dispatch])

  // END get master data

  useEffect(() => {
    getCriticalSystemConfiguration().then((res: any) => {
      if (res?.data?.data) {
        dispatch(updateCriticalConfig(res.data.data))
      }
    })

    getDeferredConfiguration().then((res: any) => {
      if (res?.data) {
        dispatch(updateDeferredConfig(res.data))
      }
    })

    return () => {}
  }, [dispatch])

  // get user Profile
  useEffect(() => {
    if (accessToken) {
      getMe().then((res: any) => {
        const newUserRole = res?.data?.userRole
        const storedUserRole = sessionStorage.getItem(lcStorageUserRole)
        
        if (storedUserRole && newUserRole && storedUserRole !== newUserRole) {
          sessionStorage.removeItem(lcStorageUserRole)
          dispatch(logOutApp())
          return
        }
        
        if (newUserRole) {
          sessionStorage.setItem(lcStorageUserRole, newUserRole)
        }
        dispatch(setUserProfile(res?.data ?? {}))
      })
      .catch(() => {
        sessionStorage.removeItem(lcStorageUserRole)
        dispatch(logOutApp())
      })
    }
  }, [accessToken, dispatch])

  const onIdle = () => {
    if (accessToken) {
      const cloneUserRole = cloneDeep(userProfile?.role ?? '')
      sessionStorage.removeItem(lcStorageUserRole)
      dispatch(logOutApp())
      setTimeout(() => {
        navigate('/log-out-inactivity', {
          state: {
            userRole: cloneUserRole,
          },
        })
      }, 300)
    }
  }

  const onMessage = (msg: string) => {}

  return (
    <ConfigProvider theme={primaryThemeDefault} locale={enUS}>
      <IdleTimerProvider
        name='app'
        timeout={1000 * APP_IDLE_TIMEOUT_SECONDS}
        onIdle={onIdle}
        onMessage={onMessage}
        crossTab
        syncTimers={200}
      >
        <Suspense fallback={'loading...'}>
          <Routes>
            <Route index path='/log-out' element={<LoggedOutBankAccountSuccess />} />
            <Route path='/' element={<Outlet />}>
              <Route
                path='/'
                index
                element={accessToken ? <Navigate to={defaultHomePage} /> : <Navigate to='/login' />}
              />

              <Route path='login' element={<AuthBaseLayout />}>
                <Route index path='/login' element={accessToken ? <Navigate to={defaultHomePage} /> : <Login />} />

                <Route
                  index
                  path='/login/otp'
                  element={accessToken ? <Navigate to={defaultHomePage} /> : <OTPLogin />}
                />
              </Route>

              <Route
                index
                path='/force-reset-password'
                element={
                  <Navigate
                    to={'/reset-password'}
                    state={{
                      resetPasswordType: ResetPasswordType.FORCE_RESET,
                      userName: searchParams?.get('userName') ?? '',
                    }}
                  />
                }
              />

              <Route path='reset-password' element={<AuthBaseLayout />}>
                <Route index path='/reset-password' element={<ResetPassword />} />
                <Route index path='/reset-password/*' element={<ResetPassword />} />
              </Route>

              <Route path='/verify/*' element={<EmailRedirectGuard />} />
              <Route index path='/page-not-found' element={<PageNotFound />} />
              <Route index path='/access-denied' element={<AccessDenied />} />
              <Route index path='/locked-user' element={<LockUserPage />} />
              <Route index path='/inactive-user' element={<InactiveUserPage />} />
              <Route index path='/log-out-inactivity' element={<LoggedOutForInactivity />} />

              <Route index path='/sso-inactive-user' element={<SSOInactiveUser />} />
              <Route index path='/sso-error-occured' element={<SSOUnexpectedErrorOccured />} />
              <Route index path='/sso-user-not-found' element={<SSOUserNotFound />} />
              <Route index path='/sso-locked-user' element={<SSOUserLocked />} />
              <Route index path='/sso-redirecting' element={<SSORedirecting />} />

              <Route element={<AuthenRouterGuard />}>
                <Route path='/' element={<AppLayout />}>
                  <Route path='home/menu' element={<Home />} />
                  <Route
                    path='bank-user-setup'
                    element={
                      <PermissionRouterGuard
                        isAllowed={userPermissions[ESubModules.BNK_USR_SET]?.includes(EPermissions.VIEW_ITEMS)}
                      >
                        <BankUserSetupFiltersProvider>
                          <LandingBankUserSetup />
                        </BankUserSetupFiltersProvider>
                      </PermissionRouterGuard>
                    }
                  ></Route>
                  <Route
                    path={menuUrls.addBankUserSetup}
                    element={
                      <PermissionRouterGuard
                        isAllowed={userPermissions[ESubModules.BNK_USR_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)}
                      >
                        <BankSetupForm />
                      </PermissionRouterGuard>
                    }
                  ></Route>
                  <Route
                    path={`${menuUrls.copyBankUserSetup}/:id`}
                    element={
                      <PermissionRouterGuard
                        isAllowed={userPermissions[ESubModules.BNK_USR_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)}
                      >
                        <BankSetupCopy />
                      </PermissionRouterGuard>
                    }
                  ></Route>
                  <Route
                    path={`${menuUrls.editBankUserSetup}/:id`}
                    element={
                      <PermissionRouterGuard
                        isAllowed={userPermissions[ESubModules.BNK_USR_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)}
                      >
                        <BankSetupEdit />
                      </PermissionRouterGuard>
                    }
                  ></Route>
                  {/* NOTE: temporarily disable this unrelease router - GBZ-3109 */}
                  {/* <Route
                    path={menuUrls.broadcastEmail}
                    element={
                      <PermissionRouterGuard
                        isAllowed={userPermissions[ESubModules.BROAD_EMAIL]?.includes(EPermissions.ADD_EDIT_ITEMS)}
                      >
                        <BroadcastEmail />
                      </PermissionRouterGuard>
                    }
                  ></Route> */}

                  <Route path={menuUrls.accountSetup}>
                    <Route
                      path={menuUrls.accountSetup}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.ACC_SET]?.includes(EPermissions.VIEW_ITEMS)}
                        >
                          <AccountSetupFiltersProvider>
                            <AccountSetup />
                          </AccountSetupFiltersProvider>
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={`${menuUrls.accountSetup}/add`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.ACC_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)}
                        >
                          <NewAccountScreen />
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={`${menuUrls.accountSetup}/view/:id`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.ACC_SET]?.includes(EPermissions.VIEW_ITEMS)}
                        >
                          <ViewAccountScreen />
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={`${menuUrls.accountSetup}/edit/:id`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.ACC_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)}
                        >
                          <EditAccountScreen />
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={`${menuUrls.viewUsersForAccount}/:id`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.ACC_SET]?.includes(EPermissions.VIEW_ITEMS)}
                        >
                          <ViewUsersForAccount />
                        </PermissionRouterGuard>
                      }
                    />
                  </Route>

                  <Route path={menuUrls.clientSetup}>
                    <Route
                      path={menuUrls.clientSetup}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.CLIENT_SETUP]?.includes(EPermissions.VIEW_ITEMS)}
                        >
                          <ClientSetupFiltersProvider>
                            <ClientSetup />
                          </ClientSetupFiltersProvider>
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={menuUrls.addClientSetup}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.CLIENT_SETUP]?.includes(EPermissions.ADD_EDIT_ITEMS)}
                        >
                          <AddClientSetup />
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={`${menuUrls.editClientSetup}/:id`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.CLIENT_SETUP]?.includes(EPermissions.ADD_EDIT_ITEMS)}
                        >
                          <EditClientSetup />
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={`${menuUrls.viewClientSetup}/:id`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.CLIENT_SETUP]?.includes(EPermissions.VIEW_ITEMS)}
                        >
                          <ClientSetupViewRecord />
                        </PermissionRouterGuard>
                      }
                    />
                  </Route>
                  <Route path={`${menuUrls.payeeMatchParameters}`}>
                    <Route
                      path={`${menuUrls.payeeMatchParameters}`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.PAYEE_MATCH_PARAM]?.includes(EPermissions.VIEW_ITEMS)}
                        >
                          <PayeeMatchParameters />
                        </PermissionRouterGuard>
                      }
                    ></Route>
                    <Route
                      path={`${menuUrls.addPayeeMatchParameters}`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.PAYEE_MATCH_PARAM]?.includes(
                            EPermissions.ADD_EDIT_ITEMS,
                          )}
                        >
                          <AddNewPayeeMatchParameters />
                        </PermissionRouterGuard>
                      }
                    ></Route>
                    <Route
                      path={`${menuUrls.editPayeeMatchParameters}/:id`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.PAYEE_MATCH_PARAM]?.includes(
                            EPermissions.ADD_EDIT_ITEMS,
                          )}
                        >
                          <EditPayeeMatchParameters />
                        </PermissionRouterGuard>
                      }
                    ></Route>
                    <Route
                      path={`${menuUrls.viewPayeeMatchParameters}/:id`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.PAYEE_MATCH_PARAM]?.includes(EPermissions.VIEW_ITEMS)}
                        >
                          <ViewPayeeMatchParameters />
                        </PermissionRouterGuard>
                      }
                    ></Route>
                  </Route>

                  <Route path={menuUrls.fileMappingSetup}>
                    <Route
                      path={menuUrls.fileMappingSetup}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.FIL_MAP_SET]?.includes(EPermissions.VIEW_ITEMS)}
                        >
                          <FileMappingSetupFiltersProvider>
                            <FileMappingSetup />
                          </FileMappingSetupFiltersProvider>
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={menuUrls.addFileMapping}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.FIL_MAP_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)}
                        >
                          <AddFileMapping />
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={`${menuUrls.viewFileMapping}/:id`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.FIL_MAP_SET]?.includes(EPermissions.VIEW_ITEMS)}
                        >
                          <ViewFileMapping />
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={`${menuUrls.editFileMapping}/:id`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.FIL_MAP_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)}
                        >
                          <EditFileMapping />
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={`${menuUrls.copyFileMapping}/:id`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.FIL_MAP_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)}
                        >
                          <CopyFileMapping />
                        </PermissionRouterGuard>
                      }
                    />
                  </Route>

                  <Route
                    path={menuUrls.addNewIssuedCheck}
                    element={
                      <PermissionRouterGuard
                        isAllowed={userPermissions[ESubModules.ADD_NEW_ISSUE_CHK]?.includes(
                          EPermissions.ADD_EDIT_ITEMS,
                        )}
                      >
                        <AddNewIssuedCheckScreen />
                      </PermissionRouterGuard>
                    }
                  />
                  <Route
                    path={menuUrls.submitIssuedCheckFile}
                    element={
                      <PermissionRouterGuard isAllowed>
                        <SubmitIssuedCheckScreen />
                      </PermissionRouterGuard>
                    }
                  />
                  <Route
                    path={menuUrls.voidChecks}
                    element={
                      <PermissionRouterGuard isAllowed>
                        <VoidChecksScreen />
                      </PermissionRouterGuard>
                    }
                  />
                  <Route
                    path={menuUrls.allTransactions}
                    element={
                      <TransactionFiltersProvider>
                        <Outlet />
                      </TransactionFiltersProvider>
                    }
                  >
                    <Route
                      path={menuUrls.allTransactions}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.ALL_TRANS]?.includes(EPermissions.VIEW_ITEMS)}
                        >
                          <AllTransactionScreen />
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={menuUrls.searchResult}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.ALL_TRANS]?.includes(EPermissions.VIEW_ITEMS)}
                        >
                          <SearchResultScreen />
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={`${menuUrls.viewTransaction}/:id`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.ALL_TRANS]?.includes(EPermissions.VIEW_ITEMS)}
                        >
                          <ViewTransactionContent />
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={`${menuUrls.viewAuditHistory}/:id`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.ALL_TRANS]?.includes(EPermissions.VIEW_ITEMS)}
                        >
                          <ViewAuditHistory />
                        </PermissionRouterGuard>
                      }
                    />
                  </Route>

                  <Route
                    path={menuUrls.batchProcessing}
                    element={
                      <PermissionRouterGuard
                        isAllowed={userPermissions[ESubModules.BAT_PROCESS]?.includes(EPermissions.VIEW_ITEMS)}
                      >
                        <BatchProcessing />
                      </PermissionRouterGuard>
                    }
                  />

                  {/* <Route
                    path={menuUrls.dataImportProcessing}
                    element={
                      <PermissionRouterGuard
                        isAllowed={userPermissions[ESubModules.DATA_IMPORT_PROCESS]?.includes(EPermissions.VIEW_ITEMS)}
                      >
                        <DataImportProcessing />
                      </PermissionRouterGuard>
                    }
                  /> */}

                  <Route
                    path={menuUrls.checkVerification}
                    element={
                      <PermissionRouterGuard
                        isAllowed={userPermissions[ESubModules.CHK_VERIFY]?.includes(EPermissions.VIEW_ITEMS)}
                      >
                        <CheckVerification />
                      </PermissionRouterGuard>
                    }
                  />

                  <Route path={menuUrls.userRoles}>
                    <Route
                      path={menuUrls.userRoles}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.USER_ROLE]?.includes(EPermissions.VIEW_ITEMS)}
                        >
                          <UserRoles />
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={`${menuUrls.userRoles}/add`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.USER_ROLE]?.includes(EPermissions.ADD_EDIT_ITEMS)}
                        >
                          <AddUserRole />
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={`${menuUrls.viewUserRole}/:id`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.USER_ROLE]?.includes(EPermissions.VIEW_ITEMS)}
                        >
                          <ViewUserRole />
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={`${menuUrls.editUserRole}/:id`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.USER_ROLE]?.includes(EPermissions.ADD_EDIT_ITEMS)}
                        >
                          <EditUserRole />
                        </PermissionRouterGuard>
                      }
                    />
                  </Route>
                  <Route path={menuUrls.sentEmailTexts}>
                    <Route
                      path={menuUrls.sentEmailTexts}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.SENT_EMAIL_TXT_LOG]?.includes(EPermissions.VIEW_ITEMS)}
                        >
                          <SentEmailTextLogsSetup />
                        </PermissionRouterGuard>
                      }
                    ></Route>
                    <Route
                      path={menuUrls.sentEmailTextsResults}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.SENT_EMAIL_TXT_LOG]?.includes(EPermissions.VIEW_ITEMS)}
                        >
                          <SentEmailTextsResults />
                        </PermissionRouterGuard>
                      }
                    ></Route>
                  </Route>
                  <Route
                    path={menuUrls.auditTrailLog}
                    element={
                      <PermissionRouterGuard
                        isAllowed={userPermissions[ESubModules.AUDIT_TRAIL_LOG]?.includes(EPermissions.VIEW_ITEMS)}
                      >
                        <AuditTrailLog />
                      </PermissionRouterGuard>
                    }
                  />
                  {/* NOTE: temporarily disable this unrelease router - GBZ-3109 */}
                  <Route
                    path={menuUrls.purgeTransactions}
                    element={
                      <PermissionRouterGuard
                        isAllowed={userPermissions[ESubModules.PURGE_TRANS]?.includes(EPermissions.VIEW_ITEMS)}
                      >
                        <PurgeTransactions />
                      </PermissionRouterGuard>
                    }
                  ></Route>
                  <Route
                    path={menuUrls.systemWideRules}
                    element={
                      <PermissionRouterGuard
                        isAllowed={userPermissions[ESubModules.SYS_WIDE_RULES]?.includes(EPermissions.VIEW_ITEMS)}
                      >
                        <SystemWideRules />
                      </PermissionRouterGuard>
                    }
                  ></Route>
                  <Route path={menuUrls.achExceptionRules}>
                    <Route
                      path={menuUrls.achExceptionRules}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.ACH_EXCEPT_RUL]?.includes(EPermissions.VIEW_ITEMS)}
                        >
                          <LandingACHExceptionRules />
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={`${menuUrls.achExceptionRulesView}/:id`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.ACH_EXCEPT_RUL]?.includes(EPermissions.VIEW_ITEMS)}
                        >
                          <ViewACHExceptionRule />
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={menuUrls.achExceptionRulesAdd}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.ACH_EXCEPT_RUL]?.includes(
                            EPermissions.ADD_ACH_EXCEPT_RUL,
                          )}
                        >
                          <AddACHExceptionRule />
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={`${menuUrls.achExceptionRulesEdit}/:id`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.ACH_EXCEPT_RUL]?.includes(EPermissions.ADD_EDIT_ITEMS)}
                        >
                          <EditACHExceptionRule />
                        </PermissionRouterGuard>
                      }
                    />
                  </Route>
                  <Route path={menuUrls.clientUserSetup}>
                    <Route
                      path={menuUrls.clientUserSetup}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.CLIENT_USR_SET]?.includes(EPermissions.VIEW_ITEMS)}
                        >
                          <ClientUserSetupFiltersProvider>
                            <LandingClientUserSetup />
                          </ClientUserSetupFiltersProvider>
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={`${menuUrls.clientUserSetup}/add`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.CLIENT_USR_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)}
                        >
                          <AddClientUserSetup />
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={`${menuUrls.copyClientUserSetup}/:id`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.CLIENT_USR_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)}
                        >
                          <AddClientUserSetup />
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={`${menuUrls.editClientUserSetup}/:id`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.CLIENT_USR_SET]?.includes(EPermissions.ADD_EDIT_ITEMS)}
                        >
                          <EditClientUserSetup />
                        </PermissionRouterGuard>
                      }
                    />
                  </Route>
                  <Route path={menuUrls.issuedCheckProcessingLog}>
                    <Route
                      path={menuUrls.issuedCheckProcessingLog}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.ISSUE_CHK_PROCESS_LOG]?.includes(
                            EPermissions.VIEW_ITEMS,
                          )}
                        >
                          <IssuedCheckProcessingLogScreen />
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={menuUrls.resultScreen}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.ISSUE_CHK_PROCESS_LOG]?.includes(
                            EPermissions.VIEW_ITEMS,
                          )}
                        >
                          <IssuedCheckLogResultScreen />
                        </PermissionRouterGuard>
                      }
                    />
                    <Route
                      path={`${menuUrls.viewErrorDetail}/:id`}
                      element={
                        <PermissionRouterGuard
                          isAllowed={userPermissions[ESubModules.ISSUE_CHK_PROCESS_LOG]?.includes(
                            EPermissions.VIEW_ITEMS,
                          )}
                        >
                          <ViewErrorScreen />
                        </PermissionRouterGuard>
                      }
                    />
                  </Route>
                  <Route
                    path={`${menuUrls.quickExceptionProcessing}`}
                    element={
                      <PermissionRouterGuard
                        isAllowed={userPermissions[ESubModules.EXCEPT_PROCESS]?.includes(EPermissions.VIEW_ITEMS)}
                      >
                        <QuickExceptionSetup />
                      </PermissionRouterGuard>
                    }
                  />
                </Route>

                <Route path={menuUrls.helpPageManagement}>
                  <Route
                    path={menuUrls.helpPageManagement}
                    element={
                      <PermissionRouterGuard
                        isAllowed={userPermissions[ESubModules.HELP_PAGE_MANAGE]?.includes(EPermissions.VIEW_ITEMS)}
                      >
                        <HelpPageManagement />
                      </PermissionRouterGuard>
                    }
                  />
                  <Route
                    path={`${menuUrls.helpPageManagement}/add`}
                    element={
                      <PermissionRouterGuard
                        isAllowed={userPermissions[ESubModules.HELP_PAGE_MANAGE]?.includes(EPermissions.ADD_EDIT_ITEMS)}
                      >
                        <AddHelpPage />
                      </PermissionRouterGuard>
                    }
                  />
                  <Route
                    path={`${menuUrls.helpPageManagement}/edit/:id`}
                    element={
                      <PermissionRouterGuard
                        isAllowed={userPermissions[ESubModules.HELP_PAGE_MANAGE]?.includes(EPermissions.ADD_EDIT_ITEMS)}
                      >
                        <EditHelpPage />
                      </PermissionRouterGuard>
                    }
                  />
                  <Route
                    path={`${menuUrls.helpPageManagement}/view/:id`}
                    element={
                      <PermissionRouterGuard
                        isAllowed={userPermissions[ESubModules.HELP_PAGE_MANAGE]?.includes(EPermissions.VIEW_ITEMS)}
                      >
                        <ViewHelpPage />
                      </PermissionRouterGuard>
                    }
                  />
                  <Route
                    path={`${menuUrls.helpPageManagement}/preview/:id`}
                    element={
                      <PermissionRouterGuard
                        isAllowed={userPermissions[ESubModules.HELP_PAGE_MANAGE]?.includes(EPermissions.VIEW_ITEMS)}
                      >
                        <PreviewHelpPage mode={'preview'} />
                      </PermissionRouterGuard>
                    }
                  />
                  <Route
                    path={`${menuUrls.helpPageManagement}/view-version-history/:id`}
                    element={
                      <PermissionRouterGuard
                        isAllowed={userPermissions[ESubModules.HELP_PAGE_MANAGE]?.includes(EPermissions.VIEW_ITEMS)}
                      >
                        <ViewVersionHistory />
                      </PermissionRouterGuard>
                    }
                  />
                </Route>

                <Route path={`/help-pages/*`} element={<PreviewHelpPage mode={'view'} />} />
              </Route>
            </Route>
            <Route path={`${menuUrls.pageNotFound}`} element={<PageNotFound />} />
            <Route path={`${menuUrls.accessDenied}`} element={<AccessDenied />} />
            <Route path='/*' element={<Navigate to={`${menuUrls.pageNotFound}`} replace />} />
          </Routes>
        </Suspense>
      </IdleTimerProvider>
    </ConfigProvider>
  )
}

export default App
